import { isReference } from '@apollo/client';

export type GraphQLResult = { __typename: string };

export const isType = <
  Result extends GraphQLResult,
  Typename extends Result['__typename'],
>(
  result: Result | null | undefined,
  typename: Typename
): result is Extract<Result, { __typename: Typename }> => {
  return result?.__typename === typename;
};

export const isTypes = <
  Result extends GraphQLResult,
  Typenames extends readonly Result['__typename'][],
>(
  result: Result | null | undefined,
  typenames: Typenames
): result is Extract<Result, { __typename: Typenames[number] }> => {
  return !!result && typenames.includes(result.__typename);
};

export const isTypeFn = <
  Result extends GraphQLResult,
  Typename extends Result['__typename'],
>(
  typename: Typename
) => {
  const predicate = (
    result: Result | null | undefined
  ): result is Extract<Result, { __typename: Typename }> =>
    result?.__typename === typename;
  return predicate;
};

export const isTypesFn = <
  Result extends GraphQLResult,
  Typenames extends readonly Result['__typename'][],
>(
  typenames: Typenames
) => {
  const predicate = (
    result: Result | null | undefined
  ): result is Extract<Result, { __typename: Typenames[number] }> =>
    !!result && typenames.includes(result.__typename);
  return predicate;
};

/* #__NO_SIDE_EFFECTS__ */
export const withFragments = <T extends NonNullable<unknown>, U>(
  func: T,
  fragments: U
): T & { fragments: U } => {
  return Object.assign(func, { fragments });
};

export const replaceByIncoming = (_existing: any[], incoming: any[]) =>
  incoming;

type UnnormalizedObject = { __typename: string; [field: string]: unknown };

// JSON scalars are plain objects and must be replaced, not merged.
const isUnnormalizedObject = (value: unknown): value is UnnormalizedObject =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !isReference(value) &&
  '__typename' in value;

const mergeUnnormalizedValues = (
  existing: unknown,
  incoming: unknown
): unknown => {
  if (Array.isArray(existing) && Array.isArray(incoming)) {
    return existing.length === incoming.length
      ? incoming.map((item, index) =>
          mergeUnnormalizedValues(existing[index], item)
        )
      : incoming;
  }

  if (
    !isUnnormalizedObject(existing) ||
    !isUnnormalizedObject(incoming) ||
    existing.__typename !== incoming.__typename
  ) {
    return incoming;
  }

  return {
    ...existing,
    ...Object.fromEntries(
      Object.entries(incoming).map(([field, value]) => [
        field,
        mergeUnnormalizedValues(existing[field], value),
      ])
    ),
  };
};

// Apollo runs nested merge functions on array items without their existing
// value, so each item is merged deeply here instead.
export const mergeArrayOfUnnormalizedObjects = (
  existing: any[],
  incoming: any[],
  { mergeObjects }: { mergeObjects: (a: any, b: any) => any }
) => {
  if (!existing || existing.length !== incoming.length) {
    return incoming;
  }
  return incoming.map((item, index) =>
    isUnnormalizedObject(item)
      ? mergeUnnormalizedValues(existing[index], item)
      : mergeObjects(existing[index], item)
  );
};

export interface GraphQLError {
  path?: string[] | null;
  message: string;
  code?: number | string | null;
}

export const formatGqlErrors = (errors: GraphQLError[]) => {
  return errors.map(e => e.message);
};

export const randomizedPollInterval = (target_interval_minutes: number) => {
  return Math.floor(
    target_interval_minutes * 60 * 1000 * (0.95 + Math.random() * 0.1) // +/- 5%
  );
};
